import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { aiTestingAgentAPI } from "../services/api";

const AgentContext = createContext(null);

export const AgentProvider = ({ children }) => {
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(() => {
    try {
      const stored = localStorage.getItem("selectedAgent");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const agentsFetchedForWorkspace = useRef(null);
  const { currentWorkspace } = useAuth();

  // Force refetch even if we've already loaded for this workspace. Callers
  // use this after mutations (agent save, vendor sync) so downstream pages
  // see fresh data without a hard browser reload.
  const refreshAgents = useCallback(async () => {
    if (!currentWorkspace) return;
    setAgentsLoading(true);
    try {
      const response = await aiTestingAgentAPI.getAllAgents(currentWorkspace.id);
      setAgents(response || []);
      agentsFetchedForWorkspace.current = currentWorkspace.id;
      // Keep selectedAgent pointing at the freshly-fetched version if it still exists
      setSelectedAgent((prev) => {
        if (!prev) return prev;
        const fresh = response?.find((a) => a.id === prev.id);
        if (fresh) {
          localStorage.setItem("selectedAgent", JSON.stringify(fresh));
          return fresh;
        }
        return prev;
      });
    } catch (err) {
      console.error("Error refreshing agents:", err);
    } finally {
      setAgentsLoading(false);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    const fetchAgents = async () => {
      if (!currentWorkspace) return;
      if (agentsFetchedForWorkspace.current === currentWorkspace.id) return;
      setAgentsLoading(true);
      try {
        const response = await aiTestingAgentAPI.getAllAgents(currentWorkspace.id);
        setAgents(response || []);
        agentsFetchedForWorkspace.current = currentWorkspace.id;
        const stored = localStorage.getItem("selectedAgent");
        const parsed = stored ? JSON.parse(stored) : null;
        const belongsToWorkspace = parsed && response?.some((a) => a.id === parsed.id);
        if (belongsToWorkspace) {
          // Refresh the stored agent data in case it changed
          const fresh = response.find((a) => a.id === parsed.id);
          setSelectedAgent(fresh);
          localStorage.setItem("selectedAgent", JSON.stringify(fresh));
        } else if (response?.length > 0) {
          const defaultAgent = response[0];
          setSelectedAgent(defaultAgent);
          localStorage.setItem("selectedAgent", JSON.stringify(defaultAgent));
        } else {
          setSelectedAgent(null);
          localStorage.removeItem("selectedAgent");
        }
      } catch (err) {
        console.error("Error fetching agents:", err);
      } finally {
        setAgentsLoading(false);
      }
    };
    fetchAgents();
  }, [currentWorkspace]);

  useEffect(() => {
    if (selectedAgent) {
      localStorage.setItem("selectedAgent", JSON.stringify(selectedAgent));
    }
  }, [selectedAgent]);

  return (
    <AgentContext.Provider value={{ agents, agentsLoading, selectedAgent, setSelectedAgent, refreshAgents }}>
      {children}
    </AgentContext.Provider>
  );
};

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) throw new Error("useAgent must be used within AgentProvider");
  return context;
};
